import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useTranslation } from '../../contexts/LanguageContext';
import { formatApiError } from '../../services/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import LanguageSwitcher from '../../components/LanguageSwitcher';
import { AlertCircle, Loader2, Headphones, CheckCircle2 } from 'lucide-react';

export default function TrialSignupPage() {
  const [formData, setFormData] = useState({
    companyName: '',
    adminName: '',
    email: '',
    password: '',
    phone: '',
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { signupTrial } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();

  const TRIAL_INCLUDES = [t('trial_include_1'), t('trial_include_2'), t('trial_include_3')];

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (formData.password.length < 6) {
      setError(t('password_min_length_error'));
      return;
    }

    setLoading(true);
    try {
      await signupTrial(formData);
      navigate('/');
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F4F4F5] flex items-center justify-center p-4">
      <div className="w-full max-w-md animate-fade-up">
        <div className="flex justify-end mb-2">
          <LanguageSwitcher variant="light" />
        </div>
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-[#002FA7] text-white mb-4">
            <Headphones className="w-8 h-8" />
          </div>
          <h1 className="text-3xl font-medium tracking-tight text-[#0F172A]" style={{ fontFamily: 'Outfit, sans-serif' }}>
            {t('trial_hero_title')}
          </h1>
          <p className="text-[#71717A] mt-2">{t('trial_hero_subtitle')}</p>
        </div>

        <Card className="border border-[#E4E4E7] shadow-none">
          <CardHeader className="space-y-1 pb-4">
            <CardTitle className="text-2xl font-medium" style={{ fontFamily: 'Outfit, sans-serif' }}>
              {t('trial_card_title')}
            </CardTitle>
            <CardDescription>{t('trial_card_subtitle')}</CardDescription>
          </CardHeader>
          <CardContent>
            <ul className="space-y-2 mb-5">
              {TRIAL_INCLUDES.map((item) => (
                <li key={item} className="flex items-start gap-2 text-sm text-[#3F3F46]">
                  <CheckCircle2 className="w-4 h-4 mt-0.5 text-[#16A34A] shrink-0" />
                  {item}
                </li>
              ))}
            </ul>

            <form onSubmit={handleSubmit} className="space-y-4">
              {error && (
                <div className="flex items-center gap-2 p-3 text-sm text-[#DC2626] bg-red-50 border border-red-200 rounded-md" data-testid="trial-signup-error">
                  <AlertCircle className="w-4 h-4" />
                  {error}
                </div>
              )}

              <div className="space-y-2">
                <Label htmlFor="companyName">{t('field_company_name')}</Label>
                <Input
                  id="companyName"
                  name="companyName"
                  type="text"
                  placeholder="Rossi Impianti Srl"
                  value={formData.companyName}
                  onChange={handleChange}
                  required
                  data-testid="trial-company-input"
                  className="border-[#E4E4E7] focus:border-[#002FA7] focus:ring-[#002FA7]"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="adminName">{t('field_your_name')}</Label>
                <Input
                  id="adminName"
                  name="adminName"
                  type="text"
                  placeholder="Mario Rossi"
                  value={formData.adminName}
                  onChange={handleChange}
                  required
                  data-testid="trial-name-input"
                  className="border-[#E4E4E7] focus:border-[#002FA7] focus:ring-[#002FA7]"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="email">{t('field_work_email')}</Label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  placeholder="mario@rossiimpianti.it"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  data-testid="trial-email-input"
                  className="border-[#E4E4E7] focus:border-[#002FA7] focus:ring-[#002FA7]"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="password">{t('field_password')}</Label>
                <Input
                  id="password"
                  name="password"
                  type="password"
                  placeholder="••••••••"
                  value={formData.password}
                  onChange={handleChange}
                  required
                  data-testid="trial-password-input"
                  className="border-[#E4E4E7] focus:border-[#002FA7] focus:ring-[#002FA7]"
                />
              </div>

              <Button
                type="submit"
                className="w-full bg-[#002FA7] hover:bg-[#002FA7]/90 text-white font-medium"
                disabled={loading}
                data-testid="trial-submit-btn"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    {t('trial_activating')}
                  </>
                ) : (
                  t('trial_activate_button')
                )}
              </Button>
            </form>

            <div className="mt-6 text-center text-sm">
              <span className="text-[#71717A]">{t('have_account')} </span>
              <Link to="/login" className="text-[#002FA7] hover:underline font-medium" data-testid="trial-login-link">
                {t('login_button')}
              </Link>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
